import React, { useMemo, useImperativeHandle, forwardRef } from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  GridOptions,
  ModuleRegistry,
  AllCommunityModule,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useTheme } from '../../../hooks/useTheme';
import { palette } from '../../../utils/palette';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

interface User {
  id: number;
  email: string;
  name?: string;
  created_at: string;
  last_login?: string;
  subscription_plan: string;
  subscription_status: string;
  is_active: boolean;
  is_admin: boolean;
  role?: string;
  account_status?: string;
  age_verified?: boolean;
}

interface UsersGridProps {
  users: User[];
  searchValue?: string;
  onBanUser: (userId: number) => void;
  onActivateUser: (userId: number) => void;
  onSuspendUser?: (userId: number) => void;
  onDeleteUser?: (userId: number) => void;
  onViewDetails?: (userId: number) => void;
  onUpdateRole?: (userId: number, role: string) => void;
}

const UsersGrid = forwardRef<any, UsersGridProps>(
  (
    {
      users,
      searchValue = '',
      onBanUser,
      onActivateUser,
      onSuspendUser,
      onDeleteUser,
      onViewDetails,
      onUpdateRole,
    },
    ref
  ) => {
    const { colors } = useTheme();
    const gridRef = React.useRef<AgGridReact>(null);
    const gridApiRef = React.useRef<any>(null);

    // Only render on web
    if (Platform.OS !== 'web') {
      return null;
    }

    // Filter users based on search value
    const filteredUsers = React.useMemo(() => {
      if (!searchValue.trim()) return users;
      const lowerSearch = searchValue.toLowerCase();
      return users.filter((user) => {
        const searchFields = [
          user.email,
          user.name,
          user.subscription_plan,
          user.subscription_status,
          user.role,
          user.account_status,
          user.id?.toString(),
        ];
        return searchFields.some(
          (field) => field && String(field).toLowerCase().includes(lowerSearch)
        );
      });
    }, [users, searchValue]);

    const columnDefs: ColDef[] = useMemo(
      () => [
        { field: 'id', headerName: 'ID', width: 80, sortable: true },
        {
          field: 'email',
          headerName: 'Email',
          flex: 1,
          sortable: true,
          filter: true,
        },
        {
          field: 'name',
          headerName: 'Name',
          flex: 1,
          sortable: true,
          filter: true,
        },
        {
          field: 'role',
          headerName: 'Role',
          width: 100,
          sortable: true,
          filter: true,
          cellRenderer: (params: any) => {
            const role = params.value || 'user';
            const colors: { [key: string]: string } = {
              admin: palette.brightGreen,
              moderator: palette.lightOrange,
              user: palette.darkGrayBlue,
            };
            return (
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: '4px',
                  backgroundColor: `${colors[role] || colors.user}20`,
                  color: colors[role] || colors.user,
                  fontSize: '11px',
                  fontWeight: '500',
                }}
              >
                {role}
              </span>
            );
          },
        },
        {
          field: 'account_status',
          headerName: 'Account Status',
          width: 120,
          sortable: true,
          filter: true,
          cellRenderer: (params: any) => {
            const status = params.value || 'active';
            const colors: { [key: string]: string } = {
              active: palette.cyanLimeGreen,
              suspended: palette.lightOrange,
              deleted: palette.brightMatteRed,
            };
            return (
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: '4px',
                  backgroundColor: `${colors[status] || colors.active}20`,
                  color: colors[status] || colors.active,
                  fontSize: '11px',
                  fontWeight: '500',
                }}
              >
                {status}
              </span>
            );
          },
        },
        {
          field: 'created_at',
          headerName: 'Created',
          width: 180,
          sortable: true,
          valueFormatter: (params) => {
            if (!params.value) return '';
            return new Date(params.value).toLocaleString();
          },
        },
        {
          field: 'last_login',
          headerName: 'Last Login',
          width: 180,
          sortable: true,
          valueFormatter: (params) => {
            if (!params.value) return 'Never';
            return new Date(params.value).toLocaleString();
          },
        },
        {
          field: 'subscription_plan',
          headerName: 'Plan',
          width: 100,
          sortable: true,
          filter: true,
        },
        {
          field: 'subscription_status',
          headerName: 'Sub Status',
          width: 100,
          sortable: true,
          filter: true,
        },
        {
          headerName: 'Actions',
          width: 280,
          cellRenderer: (params: any) => {
            const user = params.data;
            const accountStatus = user.account_status || 'active';
            return (
              <div
                style={{
                  display: 'flex',
                  gap: '4px',
                  alignItems: 'center',
                  height: '100%',
                  flexWrap: 'wrap',
                }}
              >
                {onViewDetails && (
                  <button
                    onClick={() => onViewDetails(user.id)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: palette.brightBlue,
                      color: palette.white,
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '10px',
                    }}
                    title="View Details"
                  >
                    View
                  </button>
                )}
                {accountStatus === 'active' && onSuspendUser && (
                  <button
                    onClick={() => onSuspendUser(user.id)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: palette.lightOrange,
                      color: palette.white,
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '10px',
                    }}
                    title="Suspend"
                  >
                    Suspend
                  </button>
                )}
                {accountStatus === 'suspended' && onActivateUser && (
                  <button
                    onClick={() => onActivateUser(user.id)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: palette.cyanLimeGreen,
                      color: palette.white,
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '10px',
                    }}
                    title="Activate"
                  >
                    Activate
                  </button>
                )}
                {accountStatus !== 'deleted' && onDeleteUser && (
                  <button
                    onClick={() => onDeleteUser(user.id)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: palette.brightMatteRed,
                      color: palette.white,
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '10px',
                    }}
                    title="Delete"
                  >
                    Delete
                  </button>
                )}
                {onUpdateRole && (
                  <select
                    value={user.role || 'user'}
                    onChange={(e) => onUpdateRole(user.id, e.target.value)}
                    style={{
                      padding: '4px 6px',
                      fontSize: '10px',
                      borderRadius: '4px',
                      border: `1px solid ${colors.border}`,
                      backgroundColor: colors.card,
                      color: colors.text,
                      cursor: 'pointer',
                    }}
                  >
                    <option value="user">User</option>
                    <option value="moderator">Moderator</option>
                    <option value="admin">Admin</option>
                  </select>
                )}
              </div>
            );
          },
        },
      ],
      [
        onBanUser,
        onActivateUser,
        onSuspendUser,
        onDeleteUser,
        onViewDetails,
        onUpdateRole,
        colors,
      ]
    );

    const gridOptions: GridOptions = useMemo(
      () => ({
        theme: 'legacy',
        defaultColDef: {
          resizable: true,
          sortable: true,
          filter: true,
        },
        pagination: false,
        rowHeight: 50,
        headerHeight: 40,
        suppressCellFocus: true,
        animateRows: true,
        suppressPaginationPanel: true,
        onGridReady: (params) => {
          gridApiRef.current = params.api;
        },
      }),
      []
    );

    // No need for useEffect - filtering is done via filteredUsers

    useImperativeHandle(ref, () => ({
      getApi: () => gridRef.current?.api,
    }));

    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <div
          className="ag-theme-alpine"
          style={
            {
              height: 'calc(100vh - 200px)',
              width: '100%',
              overflow: 'hidden',
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              '--ag-header-background-color': colors.card,
              '--ag-background-color': colors.background,
              '--ag-odd-row-background-color': colors.card,
              '--ag-row-hover-color': colors.card,
              '--ag-header-foreground-color': colors.text,
              '--ag-foreground-color': colors.text,
              '--ag-border-color': colors.border,
              '--ag-secondary-border-color': colors.border,
              '--ag-input-focus-border-color': colors.primary,
              '--ag-selected-row-background-color': `${colors.primary}20`,
              '--ag-range-selection-background-color': `${colors.primary}15`,
              '--ag-range-selection-border-color': colors.primary,
              '--ag-checkbox-checked-color': colors.primary,
              '--ag-checkbox-unchecked-color': colors.border,
              '--ag-input-disabled-background-color': colors.card,
              '--ag-input-disabled-border-color': colors.border,
              '--ag-input-disabled-foreground-color': colors.secondary,
              '--ag-panel-background-color': colors.card,
              '--ag-tool-panel-background-color': colors.card,
              '--ag-side-button-selected-background-color': colors.primary,
              '--ag-side-button-selected-foreground-color': palette.white,
            } as React.CSSProperties & { [key: string]: any }
          }
        >
          <style>{`
          .ag-theme-alpine .ag-body-viewport::-webkit-scrollbar,
          .ag-theme-alpine .ag-body-horizontal-scroll::-webkit-scrollbar {
            display: none;
          }
          .ag-theme-alpine .ag-body-viewport,
          .ag-theme-alpine .ag-body-horizontal-scroll {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .ag-theme-alpine {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
            font-size: 12px;
          }
          .ag-theme-alpine .ag-header {
            border-bottom: 1px solid ${colors.border};
            font-size: 13px;
            font-weight: 600;
          }
          .ag-theme-alpine .ag-header-cell {
            border-right: 1px solid ${colors.border};
            font-size: 13px;
            font-weight: 600;
          }
          .ag-theme-alpine .ag-header-cell-text {
            font-size: 13px;
            font-weight: 600;
          }
          .ag-theme-alpine .ag-row {
            border-bottom: 1px solid ${colors.border};
            font-size: 12px;
            font-weight: 400;
          }
          .ag-theme-alpine .ag-cell {
            border-right: 1px solid ${colors.border};
            font-size: 12px;
            font-weight: 400;
            line-height: 1.5;
          }
          .ag-theme-alpine .ag-cell-value {
            font-size: 12px;
            font-weight: 400;
          }
          .ag-theme-alpine input,
          .ag-theme-alpine select,
          .ag-theme-alpine button {
            font-size: 12px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
          }
        `}</style>
          <AgGridReact
            ref={gridRef}
            rowData={filteredUsers}
            columnDefs={columnDefs}
            gridOptions={gridOptions}
            domLayout="normal"
          />
        </div>
      </View>
    );
  }
);

UsersGrid.displayName = 'UsersGrid';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: '100%',
  },
});

export default UsersGrid;
